import { Field, FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';

import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import CardApplicationHeader from '@perpay-web/fintech/components/base/Header/CardApplicationHeader';
import CardApplicationTermsAndConditions from '@perpay-web/fintech/components/composite/CardApplicationEmploymentDetails/CardApplicationTermsAndConditions';
import CardApplicationEmploymentDetailsForm from '@perpay-web/fintech/components/composite/CardApplicationEmploymentDetailsForm/CardApplicationEmploymentDetailsForm';
import CardApplicationValueProp from '@perpay-web/fintech/components/composite/CardApplicationValueProp/CardApplicationValueProp';
import CardAutoTransferDisagreementVerification from '@perpay-web/fintech/components/composite/CardAutoTransferDisagreementVerification/CardAutoTransferDisagreementVerification';
import CardMaterialSelection from '@perpay-web/fintech/components/composite/CardMaterialSelection/CardMaterialSelection';
import CardMaterialSelectionPremium from '@perpay-web/fintech/components/composite/CardMaterialSelectionPremium/CardMaterialSelectionPremium';
import { PERSONAL_DETAILS_STEP } from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useCardApplicationContext } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import { REWARDS_PROGRAM_URL } from '@perpay-web/fintech/constants/urls';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import {
    METAL_MATERIAL,
    PLASTIC_MATERIAL,
    SILVER_MATERIAL,
} from '@perpay-web/fintech/utils/cardAccountApplicationMetalUtils';
import {
    cardEmploymentDetailsPrefilledSchema,
    cardEmploymentDetailsSchema,
} from '@perpay-web/fintech/validators/formBased';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { useMount } from '@perpay-web/hooks/useMount';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { getAnchorTargetFromUserAgent } from '@perpay-web/utils/routeUtils';
import { validateForm } from '@perpay-web/validators/formBased';

import styles from './CardApplicationEmploymentDetails.scss';

const CardApplicationEmploymentDetails = ({
    onSubmit,
    errors = {},
    partnerCode = null,
    welcomeOffer = null,
    isLoading,
    isSubmitLoading,
    onMount,
    dateOfBirth = '',
    ssn = '',
    onCompanySelect,
    isMetalCardEnabled,
    isUserInPremiumGroup,
    currentCompanyInfo = { company: '', uuid: '' },
    currentPayCycle,
    currentEstimatedNetPay,
    optedInToAutoApply = false,
    isPhoneVerified = false,
    isUserIdentityVerified,
    isExistingUserWithPassword,
    acceptedFairCreditReportingAgreement = false,
    acceptedCardHolderAgreement = false,
    isMarketplaceSignup = false,
    analyticsOnViewEmployerInfoModal,
    analyticsOnViewValueProp,
    analyticsOnCloseAutoTransferModal,
    analyticsOnClickSubmitApplication,
    analyticsOnOpenAutoTransferModal,
    onClickAgreement,
    onClickConsent,
    onClickDirectDeposit,
    onClickHeavyMetal,
    onClickLightPlastic,
    onClickPatriot,
    onClickPrivacyNotice,
    onClickPrivacyPolicy,
    onClickRewards,
    onClickTransfersAgreement,
    onToggleAcceptAll,
    onToggleAutoApply,
    onToggleCardHolderAgreement,
    onToggleCreditReportingAgreement,
    onToggleReceiveText,
    onSSIModalMount,
    onSSIModalChangeEmployer,
    onSSIModalContinue,
}) => {
    const { isModalOpen, setIsModalOpen } = useModal(false);
    const { isMobile } = useIsMobile();

    const [selfOnboardingData, setSelfOnboardingData] = useState({});
    const { setStep } = useCardApplicationContext();

    const onBackClicked = useCallback(() => {
        setStep(PERSONAL_DETAILS_STEP);
    }, [setStep]);

    useMount(() => onMount());

    const renderHeaderCallback = useCallback(
        () => (
            <CardApplicationHeader
                partnerCode={partnerCode}
                onBackClicked={
                    isMobile && isExistingUserWithPassword
                        ? onBackClicked
                        : null
                }
            />
        ),
        [partnerCode, isMobile, isExistingUserWithPassword, onBackClicked],
    );
    useFullHeader(renderHeaderCallback);

    const handleSubmit = (values) => {
        if (values.optedInToAutoApply || isModalOpen) {
            setIsModalOpen(false);
            const transformedValues = {
                ...values,
                selfOnboardingData,
            };
            onSubmit(transformedValues);
        } else {
            setIsModalOpen(true);
            analyticsOnOpenAutoTransferModal();
        }
    };

    const validationSchema = isUserIdentityVerified
        ? cardEmploymentDetailsPrefilledSchema
        : cardEmploymentDetailsSchema;

    const getDefaultMaterial = () => {
        if (isUserInPremiumGroup && isMetalCardEnabled) {
            return SILVER_MATERIAL;
        }

        if (isMetalCardEnabled) {
            return METAL_MATERIAL;
        }

        return PLASTIC_MATERIAL;
    };

    const initialValues = {
        estimatedNetPay: currentEstimatedNetPay,
        optedInToAutoApply,
        receiveText: false,
        acceptAll: false,
        payCycle: currentPayCycle || '',
        acceptedFairCreditReportingAgreement,
        acceptedCardHolderAgreement,
        selectedCompany: currentCompanyInfo,
        material: getDefaultMaterial(),
    };

    const formik = useFormik({
        initialValues: isUserIdentityVerified
            ? initialValues
            : { dateOfBirth, ssn, ...initialValues },
        enableReinitialize: true,
        onSubmit: handleSubmit,
        validate: (values) => validateForm(values, validationSchema),
    });

    useEffect(() => {
        if (errors && Object.keys(errors).length > 0 && formik.isSubmitting) {
            formik.setSubmitting(false);
        }
    }, [errors, formik]);

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    return (
        <FocusLayout className={styles['focus-layout']}>
            <FormikProvider value={formik}>
                {!isMobile && isExistingUserWithPassword ? (
                    <FocusHeader
                        leftButtonElement={
                            <BackButton onClick={onBackClicked} />
                        }
                        className={styles['focus-header']}
                    />
                ) : null}
                <FocusContent className={styles['focus-content']}>
                    <CardApplicationValueProp
                        isUserInPremiumGroup={isUserInPremiumGroup}
                        onValuePropView={() => {
                            analyticsOnViewValueProp();
                        }}
                        className={styles['value-prop']}
                        welcomeOffer={welcomeOffer}
                    />
                    <Stack className={styles['inner-stack']}>
                        <div className={styles.title}>
                            Finish your application
                        </div>
                        <div className={styles.subtitle}>
                            Your information is secure and{' '}
                            <span className={styles['subtitle--bold']}>
                                applying will not impact your credit score
                            </span>
                            <sup className={styles['disclaimer-footnote']}>
                                2
                            </sup>
                        </div>
                    </Stack>
                    <Stack className={styles['inner-stack']}>
                        <CardApplicationEmploymentDetailsForm
                            isSSIExemption
                            formik={formik}
                            errors={errors}
                            isUserIdentityVerified={isUserIdentityVerified}
                            isMarketplaceSignup={isMarketplaceSignup}
                            onCompanySelect={onCompanySelect}
                            setSelfOnboardingData={setSelfOnboardingData}
                            analyticsOnViewEmployerInfoModal={
                                analyticsOnViewEmployerInfoModal
                            }
                            onSSIModalMount={onSSIModalMount}
                            onSSIModalChangeEmployer={onSSIModalChangeEmployer}
                            onSSIModalContinue={onSSIModalContinue}
                        />
                        {isUserInPremiumGroup ? (
                            <CardMaterialSelectionPremium
                                value={formik.values.material}
                                onClickHeavyMetal={onClickHeavyMetal}
                                onClickLightPlastic={onClickLightPlastic}
                                onChange={(value) =>
                                    formik.setFieldValue('material', value)
                                }
                            />
                        ) : null}
                        {isMetalCardEnabled && !isUserInPremiumGroup ? (
                            <CardMaterialSelection
                                value={formik.values.material}
                                onClickHeavyMetal={onClickHeavyMetal}
                                onClickLightPlastic={onClickLightPlastic}
                                onChange={(value) =>
                                    formik.setFieldValue('material', value)
                                }
                            />
                        ) : null}
                        <CardApplicationTermsAndConditions
                            formik={formik}
                            isPhoneVerified={isPhoneVerified}
                            onClickTransfersAgreement={
                                onClickTransfersAgreement
                            }
                            onToggleAutoApply={onToggleAutoApply}
                            onToggleCardHolderAgreement={
                                onToggleCardHolderAgreement
                            }
                            onToggleCreditReportingAgreement={
                                onToggleCreditReportingAgreement
                            }
                            onToggleReceiveText={onToggleReceiveText}
                            onClickAgreement={onClickAgreement}
                            onClickConsent={onClickConsent}
                            onClickDirectDeposit={onClickDirectDeposit}
                            onClickPatriot={onClickPatriot}
                            onClickPrivacyNotice={onClickPrivacyNotice}
                            onClickPrivacyPolicy={onClickPrivacyPolicy}
                            onClickRewards={onClickRewards}
                        />
                        <div className={styles.disclaimer}>
                            <sup className={styles['disclaimer-footnote']}>
                                1
                            </sup>
                            &nbsp;See our{' '}
                            <CustomLink
                                action='inline-link'
                                href={REWARDS_PROGRAM_URL}
                                target={getAnchorTargetFromUserAgent()}
                                className={styles['disclaimer-link']}
                            >
                                Rewards Agreement
                            </CustomLink>{' '}
                            for more details on the Perpay Credit Card rewards
                            program.&nbsp;
                            <sup className={styles['disclaimer-footnote']}>
                                2
                            </sup>
                            Applying for a Perpay Credit Card will not impact
                            your FICO® or Vantage® scores.
                        </div>
                        {errors && errors.message ? (
                            <NonFieldErrors
                                errors={errors}
                                className={styles['non-field-errors']}
                            />
                        ) : null}
                    </Stack>
                </FocusContent>
                <FocusFooter>
                    <label
                        className={styles['label-accept-all']}
                        htmlFor='acceptAll'
                        aria-label='accept all terms'
                    >
                        <Field
                            id='acceptAll'
                            as='input'
                            className={styles.checkbox}
                            type='checkbox'
                            name='acceptAll'
                            data-e2e-test-id='accept-all-checkbox'
                            onClick={() => {
                                formik.setFieldValue(
                                    'acceptAll',
                                    !formik.values.acceptAll,
                                );
                                formik.setFieldValue(
                                    'acceptedFairCreditReportingAgreement',
                                    !formik.values.acceptAll,
                                );
                                formik.setFieldValue(
                                    'acceptedCardHolderAgreement',
                                    !formik.values.acceptAll,
                                );
                                formik.setFieldValue(
                                    'optedInToAutoApply',
                                    !formik.values.acceptAll,
                                );
                                formik.setFieldValue(
                                    'receiveText',
                                    !formik.values.acceptAll,
                                );
                                onToggleAcceptAll(!formik.values.acceptAll);
                            }}
                            checked={formik.values.acceptAll || false}
                        />
                        <div className={styles['checkbox-image']}>
                            <div className={styles['checkbox-icon']} />
                        </div>
                        <div className={styles['checkbox-text']}>
                            <div className={styles['checkbox-text-button']}>
                                Accept all
                            </div>
                        </div>
                    </label>
                    <EndToEndTestIdProvider testId='submit-application'>
                        <CardCta
                            Component={CustomButton}
                            type='submit'
                            disabled={
                                !formik.values
                                    .acceptedFairCreditReportingAgreement ||
                                !formik.values.acceptedCardHolderAgreement ||
                                !formik.isValid
                            }
                            loading={isSubmitLoading}
                            onClick={() => {
                                if (formik.values.optedInToAutoApply) {
                                    analyticsOnClickSubmitApplication({
                                        optedInToAutoApply:
                                            formik.values.optedInToAutoApply,
                                        autoTransferModal: false,
                                        ...(isMetalCardEnabled && {
                                            material: formik.values.material,
                                        }),
                                        optedInToTcpa:
                                            formik.values.receiveText,
                                    });
                                }
                                formik.submitForm();
                            }}
                            className={styles['accept-button']}
                        >
                            Submit Application
                        </CardCta>
                    </EndToEndTestIdProvider>
                    <div className={styles['end-text']}>
                        Submitting an application will not impact your credit
                        score
                        <sup className={styles['disclaimer-footnote']}>2</sup>
                    </div>
                </FocusFooter>
                {isModalOpen ? (
                    <CardAutoTransferDisagreementVerification
                        isSubmitting={isSubmitLoading}
                        onClickTransfersAgreement={onClickTransfersAgreement}
                        onClose={() => {
                            setIsModalOpen(false);
                            analyticsOnCloseAutoTransferModal();
                        }}
                        checked={formik.values.optedInToAutoApply || false}
                        onChange={(event) => {
                            formik.handleChange(event);
                            onToggleAutoApply({
                                optedInToAutoApply: event.target.checked,
                                autoTransferModal: true,
                            });
                        }}
                        onSubmit={() => {
                            analyticsOnClickSubmitApplication({
                                optedInToAutoApply:
                                    formik.values.optedInToAutoApply,
                                autoTransferModal: true,
                                ...(isMetalCardEnabled && {
                                    material: formik.values.material,
                                }),
                                optedInToTcpa: formik.values.receiveText,
                            });
                            formik.setSubmitting(true);
                            formik.submitForm();
                        }}
                    />
                ) : null}
            </FormikProvider>
        </FocusLayout>
    );
};

export default CardApplicationEmploymentDetails;
